// React & React Router
import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";

// Form Handling
import { useForm } from "react-hook-form";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { updateSliderFunc } from "../../../../store/custom-actions/sliders/UpdateSliderSlicer";
import { getSingleSliderFunc } from "../../../../store/custom-actions/sliders/GetSingleSliderSlicer";

// i18n
import { useTranslation } from "react-i18next";

// UI Components
import PageTitle from "../../../layouts/PageTitle";
import FormField from "../../../components/ui/FormField";
import TextAreaField from "../../../components/ui/TextAreaField";
import CheckboxField from "../../../components/ui/CheckboxField";
import SubmitButton from "../../../components/ui/SubmitButton";
import MediaSelectorButton from "../../../components/media/MediaSelectorButton";
import LanguageSwitcherTabs from "../../../components/ui/LanguageSwitcherTabs";

// Utilities
import { getLocalizedTranslation } from "../../../../utils/localization";

function UpdateSliderForm() {
  const { t } = useTranslation();
  const { sliderId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [activeLang, setActiveLang] = useState();
  const [imageDefault, setImageDefault] = useState([]);
  const [imageMobDefault, setImageMobDefault] = useState([]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    watch,
  } = useForm();

  const { loading: submitLoading } = useSelector(
    (state) => state.updateSliderSlicer
  );
  const { loading: languageLoading, data: languageData } = useSelector(
    (state) => state.getLanguageSettingsSlicer
  );
  const { data: sliderData, loading } = useSelector(
    (state) => state.getSingleSliderSlicer
  );

  // Fetch single slider
  useEffect(() => {
    if (sliderId) dispatch(getSingleSliderFunc(sliderId));
  }, [dispatch, sliderId]);

  // Handle language switching
  useEffect(() => {
    if (languageData?.defaultLang?.slug) {
      setActiveLang(languageData.defaultLang.slug);
    }
  }, [languageData]);

  // Populate form data after slider fetch
  useEffect(() => {
    if (!sliderData) return;
    const translations = getLocalizedTranslation(
      sliderData.translations,
      languageData?.defaultLang?.slug
    );

    setImageDefault(sliderData.img ? [sliderData.img] : []);
    setImageMobDefault(sliderData.mob_img ? [sliderData.mob_img] : []);
    reset(buildFormDefaults(sliderData, translations));
  }, [sliderData, languageData, reset]);

  // Update form when activeLang changes
  useEffect(() => {
    if (!sliderData?.translations || !activeLang) return;

    const translation = sliderData.translations.find(
      (tr) => tr.locale === activeLang
    );
    setValue("title", translation?.title || "");
    setValue("description", translation?.description || "");
    setValue("btn_text", translation?.btn_text || "");
  }, [activeLang, sliderData, setValue]);

  const onSubmit = async (data) => {
    const payload = {
      lang: activeLang,
      ...data,
    };
    const result = await dispatch(
      updateSliderFunc({ id: sliderId, data: payload })
    );
    if (updateSliderFunc.fulfilled.match(result)) {
      reset();
      navigate("/sliders-settings");
    }
  };

  /**
   * Helpers
   */
  function buildFormDefaults(slider, translations) {
    return {
      title: translations.title,
      description: translations.description,
      btn_text: slider.btn_text,
      link: slider.link,
      active: slider.active,
      image: slider.image,
      mobile_image: slider.mobile_image,
    };
  }

  return (
    <>
      <PageTitle
        motherMenu="Home"
        activeMenu="Update Slider"
        customClasses="my-0"
      />

      <div className="relative flex items-center justify-end my-3">
        <Link to="/sliders-settings">
          <button type="button" className="btn btn-secondary sw-btn-back me-2">
            {t("Back")}
          </button>
        </Link>
        <SubmitButton isLoading={submitLoading} form="submit-form" />
      </div>

      <div className="row">
        <div className="col-12">
          <LanguageSwitcherTabs
            languages={languageData?.languages || []}
            activeLang={activeLang}
            onChange={setActiveLang}
          />
        </div>

        <div className="col-12">
          <div className="card p-4">
            <form id="submit-form" onSubmit={handleSubmit(onSubmit)}>
              <div className="row">
                <div className="form-group logo-input mb-3 col-sm-12 col-md-6">
                  <FormField
                    label="Title"
                    name="title"
                    register={register}
                    errors={errors}
                  />
                </div>
                <div className="form-group mb-3 col-sm-12 col-md-6">
                  <FormField
                    label="Button Text"
                    name="btn_text"
                    register={register}
                    errors={errors}
                  />
                </div>
                <div className="form-group mb-3 col-12">
                  <FormField
                    label="Path Link"
                    name="link"
                    register={register}
                    errors={errors}
                  />
                </div>
                <div className="form-group mb-3 col-12">
                  <CheckboxField
                    name="active"
                    label="Activity"
                    register={register}
                    errors={errors}
                  />
                </div>
              </div>

              <div className="row">
                <div className="form-group mb-3 col-12">
                  <TextAreaField
                    label="Description"
                    name="description"
                    isRichTextEditor
                    register={register}
                    errors={errors}
                    watch={watch}
                    setValue={setValue}
                  />
                </div>
              </div>

              <div className="row">
                <div className="form-group mb-3 col-sm-12 col-md-6">
                  <MediaSelectorButton
                    label="Slider Image"
                    inputName="image"
                    required
                    register={register}
                    errors={errors}
                    onChange={(value) => setValue("image", value)}
                    defaultSelected={imageDefault}
                  />
                </div>
                <div className="form-group mb-3 col-sm-12 col-md-6">
                  <MediaSelectorButton
                    label="Slider Mobile Image"
                    inputName="mobile_image"
                    required
                    register={register}
                    errors={errors}
                    onChange={(value) => setValue("mobile_image", value)}
                    defaultSelected={imageMobDefault}
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default UpdateSliderForm;
